import React, { useState, useRef, useEffect } from 'react';
import type { PlayerHand, HeistDeckId } from '../../../../games/heist-city/heistDecks';
import { HEIST_CARD_BY_ID } from '../../../../games/heist-city/heistDecks';

interface HandManagementCardProps {
  hand: PlayerHand;
  onDealHand: () => void;
  onViewHand: () => void;
  readOnly?: boolean;
}

const DECK_STYLE: Record<HeistDeckId, { text: string; bg: string; border: string }> = {
  overt:     { text: 'text-red-300',    bg: 'bg-red-900/30',    border: 'border-red-500/40' },
  hidden:    { text: 'text-purple-300', bg: 'bg-purple-900/30', border: 'border-purple-500/40' },
  disguised: { text: 'text-green-300',  bg: 'bg-green-900/30',  border: 'border-green-500/40' },
};

const HandManagementCard: React.FC<HandManagementCardProps> = ({ hand, onDealHand, onViewHand, readOnly }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const confirmRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showConfirm) return;
    const handle = (e: MouseEvent) => {
      if (confirmRef.current && !confirmRef.current.contains(e.target as Node)) {
        setShowConfirm(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [showConfirm]);

  // Tally cards by deck type
  const deckCounts: Partial<Record<HeistDeckId, number>> = {};
  for (const cardId of hand.cards) {
    const card = HEIST_CARD_BY_ID[cardId];
    if (card) deckCounts[card.deck] = (deckCounts[card.deck] ?? 0) + 1;
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Hand summary badges */}
      <div className="flex items-center gap-1 text-xs">
        {hand.cards.length === 0 ? (
          <span className="text-gray-500 italic">No cards</span>
        ) : (
          <>
            <span className="text-gray-400">{hand.cards.length}:</span>
            {(Object.entries(deckCounts) as [HeistDeckId, number][]).map(([deck, count]) => (
              <span
                key={deck}
                className={`px-1.5 py-0.5 rounded border ${DECK_STYLE[deck].bg} ${DECK_STYLE[deck].text} ${DECK_STYLE[deck].border}`}
              >
                {count} {deck}
              </span>
            ))}
          </>
        )}
      </div>

      {/* Deal Hand button + dropdown confirm */}
      {!readOnly && (
        <div ref={confirmRef} className="relative">
          <button
            onClick={() => setShowConfirm(v => !v)}
            className="px-2 py-1 bg-yellow-600 hover:bg-yellow-700 text-white text-xs rounded font-semibold transition-colors"
          >
            Deal Hand
          </button>
          {showConfirm && (
            <div className="absolute top-full left-0 mt-1 z-20 w-60 p-3 bg-gray-800 border border-yellow-500/60 rounded shadow-2xl text-xs space-y-2">
              <p className="text-yellow-300 font-semibold">Deal new hand?</p>
              <p className="text-gray-400">
                Your {hand.cards.length} card{hand.cards.length !== 1 ? 's' : ''} return to the decks.
                New cards are dealt to match your party's current states.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => { onDealHand(); setShowConfirm(false); }}
                  className="flex-1 px-2 py-1 bg-yellow-600 hover:bg-yellow-700 text-white rounded font-semibold transition-colors"
                >
                  Confirm
                </button>
                <button
                  onClick={() => setShowConfirm(false)}
                  className="flex-1 px-2 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* View Hand button */}
      <button
        onClick={onViewHand}
        className="px-2 py-1 bg-purple-600 hover:bg-purple-700 text-white text-xs rounded font-semibold transition-colors"
      >
        View Hand
      </button>
    </div>
  );
};

export default HandManagementCard;
